// EL REGISTRO DE LO PENDIENTE · DIRECTRIZ-PWA F2 (11 oct 2026, DISENO-F2 §6.3).
//
// Siete sitios guardan trabajo en el teléfono y ninguno lo sabe del otro:
// movimientos (`lib/cola.js`), conteos, recepciones, lecturas del escáner,
// traslados, ventas y las capturas del importador. Aquí se LEEN las siete
// —sin tocar ninguna— y se presentan con un solo contrato para la franja,
// el chip y la pantalla «Pendientes de enviar».
//
// EL ESPEJO «POR REVISAR». Las colas viejas sueltan lo que el servidor
// rechazó (lo devuelven en `rechazados` y lo borran de su almacén), así
// que tras recargar la página ya no quedaba rastro. Aquí se guarda una
// copia (`tego-por-revisar`) con el motivo del servidor, para que la
// pantalla lo conserve hasta que la persona lo descarte. Las colas nuevas
// no lo necesitan: guardan el POR_REVISAR en su propia fila.
//
// EL ORDEN DE ENVÍO (D-7): movimientos → conteos → recepciones → traslados
// → lecturas → ventas. Una venta consume existencia que una entrada, una
// recepción o un traslado recibido acaban de crear; mandarla antes la
// rechazaría por «stock insuficiente» por puro orden. Las capturas del
// importador las manda su propia pantalla y se quedan así.
import { api } from '../../api';
import { pendientes as pendientesMovimiento, sincronizar as sincronizarMovimientos } from '../../lib/cola';
import { pendientesConteo, sincronizarConteos } from '../../lib/colaConteos';
import { pendientesRecepcion, sincronizarRecepciones } from '../../lib/colaRecepciones';
import {
  lecturas, sincronizarLecturas, quitarLectura, PENDIENTE as LECTURA_PENDIENTE,
} from '../../lib/colaLecturas';
import {
  listarTraslados, sincronizarTraslados, enviarTrasladoAhora, descartarTraslado,
  contarTrasladosDeOtraCuenta, RECIBIR,
} from '../../lib/colaTraslados';
import {
  listarVentas, sincronizarVentas, enviarVentaAhora, descartarVenta, contarVentasDeOtraCuenta,
} from '../../lib/colaVentas';
import {
  pendientesCaptura, rechazadasCaptura, descartarRechazada, llaveCola,
} from '../../importador/colaCaptura';
import {
  PENDIENTE, ENVIANDO, POR_REVISAR, ENVIADA, EVENTO_CAMBIO, INTENTOS_PARA_AVISAR,
} from '../../lib/colaBase';

export { PENDIENTE, ENVIANDO, POR_REVISAR, ENVIADA, INTENTOS_PARA_AVISAR };
export const PARA_TERMINAR = 'PARA_TERMINAR';

const dinero = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n || 0));
const num = (v) => Number(v || 0).toLocaleString('es-MX');

// Lo que hace falta hacer con un rechazo, por tipo (capa 2: pegado al
// elemento que lo necesita). Son las palabras de `AvisoRechazados.jsx` y de
// DISENO-F2 §8.
export const QUE_HACER = {
  movimiento: 'La existencia no se movió. Regístralo de nuevo desde Escanear con la cantidad que sí hay.',
  recepcion: 'La mercancía está en el almacén y no figura en el inventario. Recíbela de nuevo, o regístrala como entrada si la orden ya se cerró.',
  traslado: 'La mercancía no salió en TEGO. Vuelve a mandarla desde Traslados.',
  traslado_recibir: 'La recepción no quedó. Ábrela desde Traslados y confirma lo que llegó.',
  venta: 'La venta no quedó en TEGO y el dinero sí entró. Ábrela en Punto de venta para cobrarla con lo que haya, o descártala y anótala fuera.',
  captura: 'La captura no entró. Ábrela en el importador y revisa el dato que dice el mensaje.',
  lectura: 'Toca la lectura para terminar su movimiento con señal.',
};

// La consecuencia de descartar, por tipo (va en la confirmación).
export const CONSECUENCIA_DE_DESCARTAR = {
  movimiento: 'La existencia no se moverá en TEGO.',
  recepcion: 'La mercancía seguirá sin figurar en el inventario.',
  traslado: 'La mercancía seguirá en su origen en TEGO.',
  traslado_recibir: 'El traslado seguirá en camino en TEGO.',
  venta: 'El dinero que entró y la mercancía que salió no quedarán en TEGO: tendrás que anotarlos de otra forma.',
  captura: 'Ese producto no entrará en la migración por esta vía.',
  lectura: 'El código leído se olvida; si hace falta, vuelve a escanearlo.',
  conteo: 'El total anotado no llegará a la hoja de conteo.',
};

export const NOMBRE_DE_TIPO = {
  movimiento: 'Movimiento', conteo: 'Conteo', recepcion: 'Recepción', lectura: 'Lectura',
  traslado: 'Traslado', traslado_recibir: 'Recepción de traslado', venta: 'Venta', captura: 'Captura',
};

// ---------------------------------------------------------------------
// El espejo persistente de lo que las colas viejas rechazan
// ---------------------------------------------------------------------
const DB_ESPEJO = 'tego-por-revisar';
const STORE_ESPEJO = 'elementos';

function abrirEspejo() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB_ESPEJO, 1);
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(STORE_ESPEJO)) {
        r.result.createObjectStore(STORE_ESPEJO, { keyPath: 'clave' });
      }
    };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
function conEspejo(modo, fn) {
  return abrirEspejo().then((d) => new Promise((res, rej) => {
    const t = d.transaction(STORE_ESPEJO, modo);
    const rq = fn(t.objectStore(STORE_ESPEJO));
    t.oncomplete = () => { d.close(); res(rq && rq.result); };
    t.onerror = () => { d.close(); rej(t.error); };
    t.onabort = () => { d.close(); rej(t.error); };
  })).catch(() => null);
}

const ventana = () => globalThis.window ?? globalThis;

function avisarCambio() {
  try { ventana().dispatchEvent?.(new CustomEvent(EVENTO_CAMBIO, { detail: { db: DB_ESPEJO } })); } catch { /* sin ventana */ }
}

/** Guarda en el espejo lo que una cola vieja rechazó. `tipo`: movimiento | recepcion | conteo. */
export async function anotarPorRevisar(tipo, rechazados = [], idUsuario = null) {
  for (const r of rechazados) {
    const clave = `${tipo}:${r.clave_idempotencia || r.clave || `${r._ts || Date.now()}`}`;
    await conEspejo('readwrite', (s) => s.put({
      clave, tipo, datos: r, motivo: r._error || r.motivo || '', _ts: r._ts || Date.now(), _rechazada_en: Date.now(),
      ...(idUsuario !== null && idUsuario !== undefined ? { id_usuario: idUsuario } : {}),
    }));
  }
  if (rechazados.length) avisarCambio();
}

export async function leerPorRevisar() {
  return (await conEspejo('readonly', (s) => s.getAll())) || [];
}

export async function descartarPorRevisar(clave) {
  await conEspejo('readwrite', (s) => s.delete(clave));
  avisarCambio();
}

// ---------------------------------------------------------------------
// De quién es lo que guardan las colas viejas (P1-4 de AUDITORIA-F2)
// ---------------------------------------------------------------------
// Las cuatro colas viejas (movimientos, conteos, recepciones, lecturas) no
// guardan quién hizo cada cosa, y no se tocan (Doctrina 1). Sin esto, otra
// cuenta en el mismo teléfono veía, enviaba con SU sesión y podía descartar
// lo de la anterior. La regla, por PRIMER AVISTAMIENTO:
//
//   · cada vez que el censo corre con una sesión, se anota en este teléfono
//     qué cuenta está abierta y desde cuándo (`desde`); si cambia la cuenta,
//     `desde` vuelve a empezar;
//   · lo que aparece en una cola vieja guardado DESPUÉS de `desde` es de la
//     cuenta abierta, y queda anotado (clave → cuenta) para siempre;
//   · lo guardado ANTES y sin anotar es «sin dueño conocido»: no es de nadie
//     que se pueda probar, así que no se enseña, no se envía y no se
//     descarta desde ninguna cuenta. Se cuenta en `otraCuenta`, y quien lo
//     guardó puede reclamarlo con una confirmación (`reclamarSinDueno`).
//
// Una cola vieja se envía ENTERA (su `sincronizar` no filtra): si guarda
// algo que no es de la cuenta abierta, esa cola espera, y lo propio que haya
// en ella lo dice en su tarjeta.
const LLAVE_CUENTA = 'tego.pwa.cuenta_en_telefono';
const LLAVE_DUENOS = 'tego.pwa.duenos';
export const ESPERA_POR_OTRA_CUENTA = 'Espera: en este teléfono hay trabajo del mismo tipo guardado por otra cuenta, y se envían juntos. '
  + 'Sale solo cuando esa persona entre y lo envíe, o cuando se reclame o se descarte.';

const almacen = () => { try { return globalThis.localStorage || null; } catch { return null; } };
function leerJson(llave, vacio) {
  try { return JSON.parse(almacen()?.getItem(llave) || 'null') ?? vacio; } catch { return vacio; }
}
function escribirJson(llave, valor) {
  try { almacen()?.setItem(llave, JSON.stringify(valor)); } catch { /* almacenamiento lleno: se reintenta en el siguiente censo */ }
}

/** Anota que `usuario` tiene la sesión abierta en este teléfono. Devuelve {id, desde} o null. */
export function cuentaAbierta(usuario, ahora = Date.now()) {
  const id = usuario?.id;
  if (id === undefined || id === null || id === '') return null;
  const previa = leerJson(LLAVE_CUENTA, null);
  if (previa && String(previa.id) === String(id)) return previa;
  const nueva = { id, desde: ahora };
  escribirJson(LLAVE_CUENTA, nueva);
  return nueva;
}

function duenoDe(mapa, clave, ts, cuenta) {
  const r = mapa[clave];
  const t = Number(ts) || 0;
  if (r && t <= r.ts) return r.u;
  if (cuenta && t >= cuenta.desde) { mapa[clave] = { u: cuenta.id, ts: t }; return cuenta.id; }
  return r ? r.u : null;
}

/**
 * Reparte lo de las colas viejas entre la cuenta abierta y lo demás.
 * Devuelve {movs, conteos, recs, lects, espejo} (lo propio), y
 * {ajenas, sinDueno, bloqueadas: Set(tipo)}.
 */
function repartirViejas(usuario, { movs, conteos, recs, lects, espejo }) {
  const cuenta = cuentaAbierta(usuario);
  const mapa = leerJson(LLAVE_DUENOS, {});
  const vistas = new Set();
  const salida = { ajenas: 0, sinDueno: 0, bloqueadas: new Set() };
  const repartir = (tipo, lista, claveDe, tsDe, cuentaParaBloquear = () => true) => lista.filter((x) => {
    const clave = `${tipo}:${claveDe(x)}`;
    vistas.add(clave);
    const d = x.id_usuario ?? duenoDe(mapa, clave, tsDe(x), cuenta);
    if (cuenta && String(d) === String(cuenta.id)) return true;
    if (d === null || d === undefined) salida.sinDueno += 1; else salida.ajenas += 1;
    if (cuentaParaBloquear(x)) salida.bloqueadas.add(tipo);
    return false;
  });
  const propias = {
    movs: repartir('movimiento', movs, (m) => m.clave_idempotencia, (m) => m._ts),
    conteos: repartir('conteo', conteos, (c) => c.clave, (c) => c._ts),
    recs: repartir('recepcion', recs, (r) => r.clave_idempotencia, (r) => r._ts),
    lects: repartir('lectura', lects, (l) => l.clave_idempotencia, (l) => l._ts, (l) => l.estado === LECTURA_PENDIENTE),
    espejo: repartir('espejo', espejo, (x) => x.clave, (x) => x._rechazada_en || x._ts, () => false),
  };
  // Se olvida lo que ya no está en ninguna cola.
  for (const k of Object.keys(mapa)) if (!vistas.has(k)) delete mapa[k];
  escribirJson(LLAVE_DUENOS, mapa);
  return { ...propias, ...salida };
}

async function leerViejas() {
  const [movs, conteos, recs, lects, espejo] = await Promise.all([
    seguro(pendientesMovimiento()), seguro(pendientesConteo()), seguro(pendientesRecepcion()),
    seguro(lecturas()), seguro(leerPorRevisar()),
  ]);
  return { movs: movs || [], conteos: conteos || [], recs: recs || [], lects: lects || [], espejo: espejo || [] };
}

/**
 * «Son míos»: lo que está sin dueño conocido pasa a la cuenta abierta, que
 * desde ese momento lo ve, lo envía con su sesión y puede descartarlo.
 * Lo que ya tiene dueño (otra cuenta) NO se toca. Devuelve cuántos.
 */
export async function reclamarSinDueno(usuario) {
  const cuenta = cuentaAbierta(usuario);
  if (!cuenta) return 0;
  const viejas = await leerViejas();
  const mapa = leerJson(LLAVE_DUENOS, {});
  let n = 0;
  const reclamar = (tipo, lista, claveDe, tsDe) => {
    for (const x of lista) {
      const clave = `${tipo}:${claveDe(x)}`;
      if (x.id_usuario !== undefined && x.id_usuario !== null) continue;
      if (duenoDe(mapa, clave, tsDe(x), cuenta) === null) { mapa[clave] = { u: cuenta.id, ts: Number(tsDe(x)) || 0 }; n += 1; }
    }
  };
  reclamar('movimiento', viejas.movs, (m) => m.clave_idempotencia, (m) => m._ts);
  reclamar('conteo', viejas.conteos, (c) => c.clave, (c) => c._ts);
  reclamar('recepcion', viejas.recs, (r) => r.clave_idempotencia, (r) => r._ts);
  reclamar('lectura', viejas.lects, (l) => l.clave_idempotencia, (l) => l._ts);
  reclamar('espejo', viejas.espejo, (x) => x.clave, (x) => x._rechazada_en || x._ts);
  escribirJson(LLAVE_DUENOS, mapa);
  if (n) avisarCambio();
  return n;
}

// ---------------------------------------------------------------------
// Las siete fuentes, con un solo contrato
// ---------------------------------------------------------------------
const seguro = (p, vacio = []) => Promise.resolve(p).catch(() => vacio);

function tituloMovimiento(m) {
  const tipo = { ENTRADA: 'Entrada', SALIDA: 'Salida', TRASPASO: 'Traspaso', AJUSTE: 'Ajuste' }[m.tipo] || m.tipo || 'Movimiento';
  const destino = m.ubicacion_destino || m.ubicacion_origen || m.ubicacion || '';
  return `${tipo} · ${m.sku} × ${num(m.cantidad)}${destino ? ` → ${destino}` : ''}`;
}

function deMovimientos(lista, espera = false) {
  return lista.map((m) => ({
    tipo: 'movimiento', clave: `movimiento:${m.clave_idempotencia}`, hora: new Date(m._ts || Date.now()),
    estado: PENDIENTE, titulo: tituloMovimiento(m), detalle: m.lote ? `Lote ${m.lote}` : '',
    intentos: 0, enviarAhora: espera ? null : 'todo', espera: espera ? ESPERA_POR_OTRA_CUENTA : null,
    abrir: { modulo: 'escanear', contexto: { tipo: m.tipo }, etiqueta: 'Abrir en Escanear' },
  }));
}
function deConteos(lista, espera = false) {
  return lista.map((c) => ({
    tipo: 'conteo', clave: `conteo:${c.clave}`, hora: new Date(c._ts || Date.now()),
    estado: PENDIENTE, titulo: `Conteo: renglón ${c.id_renglon} va por ${num(c.cantidad)}`,
    detalle: `Hoja ${c.id_sesion}`, intentos: 0, enviarAhora: espera ? null : 'todo', espera: espera ? ESPERA_POR_OTRA_CUENTA : null,
    abrir: { modulo: 'conteos', contexto: { id_sesion: c.id_sesion }, etiqueta: 'Abrir en Conteos' },
  }));
}
function deRecepciones(lista, espera = false) {
  return lista.map((r) => ({
    tipo: 'recepcion', clave: `recepcion:${r.clave_idempotencia}`, hora: new Date(r._ts || Date.now()),
    estado: PENDIENTE, titulo: `Recepción de la orden ${r.id_orden} · ${(r.renglones || []).length} producto(s)`,
    detalle: '', intentos: 0, enviarAhora: espera ? null : 'todo', espera: espera ? ESPERA_POR_OTRA_CUENTA : null,
    abrir: { modulo: 'compras', contexto: { id_orden: r.id_orden }, etiqueta: 'Abrir en Compras' },
  }));
}
function deLecturas(lista, espera = false) {
  return lista.map((l) => ({
    tipo: 'lectura', clave: `lectura:${l.clave_idempotencia}`, hora: new Date(l._ts || Date.now()),
    estado: l.estado === LECTURA_PENDIENTE ? PENDIENTE : PARA_TERMINAR,
    titulo: `Código ${l.codigo} leído${l.medio === 'CAMARA' ? ' con la cámara' : ''}`,
    detalle: l.estado === LECTURA_PENDIENTE ? '' : (l.mensaje || 'TEGO ya dijo qué es: tócala para terminar.'),
    intentos: 0, enviarAhora: l.estado === LECTURA_PENDIENTE && !espera ? 'todo' : null,
    espera: l.estado === LECTURA_PENDIENTE && espera ? ESPERA_POR_OTRA_CUENTA : null,
    abrir: { modulo: 'escanear', contexto: { codigo: l.codigo, medio: l.medio || 'TECLADO', tipo: l.tipo }, etiqueta: 'Abrir en Escanear' },
    quitar: () => quitarLectura(l.clave_idempotencia),
  }));
}
function deTraslados(lista) {
  return lista.map((e) => {
    const r = e.resumen || {};
    const recibe = e.accion === RECIBIR;
    return {
      tipo: recibe ? 'traslado_recibir' : 'traslado', clave: e.clave_idempotencia, hora: new Date(e._ts),
      estado: e.estado,
      titulo: recibe
        ? `Recepción del traslado ${r.folio ?? e.id_traslado}`
        : `Traslado ${r.origen} → ${r.destino} · ${r.renglones} producto(s)`,
      detalle: recibe ? '' : `${num(r.unidades)} u.${r.referencia ? ` · ${r.referencia}` : ''}`,
      motivo: e.motivo || null, intentos: e._intentos || 0, folio: e.folio ?? null,
      enviadaEn: e._enviada_en ? new Date(e._enviada_en) : null, rechazadaEn: e._rechazada_en ? new Date(e._rechazada_en) : null,
      enviarAhora: e.estado === PENDIENTE ? () => enviarTrasladoAhora(e.clave_idempotencia) : null,
      abrir: { modulo: 'traslados', contexto: {}, etiqueta: 'Abrir en Traslados' },
      descartar: () => descartarTraslado(e.clave_idempotencia),
    };
  });
}
function deVentas(lista) {
  return lista.map((e) => {
    const r = e.resumen || {};
    return {
      tipo: 'venta', clave: e.clave_idempotencia, hora: new Date(e._ts), estado: e.estado,
      titulo: `Venta en efectivo · ${dinero(r.total)} · ${r.renglones} producto(s)`,
      detalle: r.ubicacion ? `Sale de ${r.ubicacion}` : '',
      motivo: e.motivo || null, intentos: e._intentos || 0, folio: e.folio ?? null,
      enviadaEn: e._enviada_en ? new Date(e._enviada_en) : null, rechazadaEn: e._rechazada_en ? new Date(e._rechazada_en) : null,
      enviarAhora: e.estado === PENDIENTE ? () => enviarVentaAhora(e.clave_idempotencia) : null,
      abrir: { modulo: 'pos', contexto: { carrito_local: e.clave_idempotencia }, etiqueta: 'Abrir en Punto de venta' },
      descartar: () => descartarVenta(e.clave_idempotencia),
    };
  });
}
function llavesDeCaptura(usuario) {
  const prefijo = `tego.migracion.captura:`;
  const salida = [];
  try {
    const n = globalThis.localStorage?.length ?? 0;
    for (let i = 0; i < n; i += 1) {
      const k = globalThis.localStorage.key(i);
      if (k && k.startsWith(prefijo) && !k.startsWith(`${prefijo.slice(0, -1)}.ubicaciones`)) {
        const partes = k.split(':');
        const idMigracion = partes[partes.length - 1];
        if (llaveCola(usuario, idMigracion) === k) salida.push({ llave: k, idMigracion });
      }
    }
  } catch { /* sin almacenamiento */ }
  return salida;
}
function deCapturas(usuario) {
  const salida = [];
  for (const { llave, idMigracion } of llavesDeCaptura(usuario)) {
    for (const c of pendientesCaptura(llave)) {
      salida.push({
        tipo: 'captura', clave: `captura:${c.id_operacion}`, hora: new Date(c._ts || Date.now()), estado: PENDIENTE,
        titulo: `Captura de la migración ${idMigracion} · ${c.sku || c.codigo || ''}`, detalle: c.nombre || '',
        intentos: 0, enviarAhora: null,
        abrir: { modulo: 'migracion', contexto: { id_migracion: idMigracion }, etiqueta: 'Abrir en el importador' },
      });
    }
    for (const c of rechazadasCaptura(llave)) {
      salida.push({
        tipo: 'captura', clave: `captura:${c.id_operacion}`, hora: new Date(c._ts || Date.now()), estado: POR_REVISAR,
        titulo: `Captura de la migración ${idMigracion} · ${c.sku || c.codigo || ''}`, detalle: c.nombre || '',
        motivo: c.motivo || '', intentos: 0, enviarAhora: null,
        abrir: { modulo: 'migracion', contexto: { id_migracion: idMigracion }, etiqueta: 'Abrir en el importador' },
        descartar: async () => descartarRechazada(llave, c.id_operacion),
      });
    }
  }
  return salida;
}
function delEspejo(lista) {
  return lista.map((x) => {
    const d = x.datos || {};
    const base = {
      clave: x.clave, hora: new Date(x._ts || Date.now()), estado: POR_REVISAR, motivo: x.motivo || '',
      rechazadaEn: new Date(x._rechazada_en || Date.now()), intentos: 0, enviarAhora: null,
      descartar: () => descartarPorRevisar(x.clave),
    };
    if (x.tipo === 'recepcion') {
      return { ...base, tipo: 'recepcion', titulo: `Recepción de la orden ${d.id_orden} · ${(d.renglones || []).length} producto(s)`, detalle: '',
        abrir: { modulo: 'compras', contexto: { id_orden: d.id_orden }, etiqueta: 'Abrir en Compras' } };
    }
    return { ...base, tipo: 'movimiento', titulo: tituloMovimiento(d), detalle: '',
      abrir: { modulo: 'escanear', contexto: { tipo: d.tipo, sku: d.sku }, etiqueta: 'Abrir en Escanear' } };
  });
}

/**
 * Todo lo guardado en este teléfono, con un solo contrato:
 *   { elementos: [{tipo, clave, hora, estado, titulo, detalle, motivo?, intentos,
 *                  folio?, enviadaEn?, rechazadaEn?, enviarAhora, abrir, descartar?, quitar?}],
 *     otraCuenta: n }
 */
export async function censoDePendientes(usuario) {
  const id = usuario?.id;
  const [viejas, tras, vtas, otraT, otraV] = await Promise.all([
    leerViejas(), seguro(listarTraslados(id)), seguro(listarVentas(id)),
    seguro(contarTrasladosDeOtraCuenta(id), 0), seguro(contarVentasDeOtraCuenta(id), 0),
  ]);
  const v = repartirViejas(usuario, viejas);
  const b = v.bloqueadas;
  const elementos = [
    ...deMovimientos(v.movs, b.has('movimiento')), ...deConteos(v.conteos, b.has('conteo')),
    ...deRecepciones(v.recs, b.has('recepcion')),
    ...deTraslados(tras || []), ...deLecturas(v.lects, b.has('lectura')), ...deVentas(vtas || []),
    ...deCapturas(usuario), ...delEspejo(v.espejo),
  ];
  elementos.forEach((e) => { e.queHacer = QUE_HACER[e.tipo] || ''; e.consecuencia = CONSECUENCIA_DE_DESCARTAR[e.tipo] || ''; });
  return {
    elementos,
    otraCuenta: Number(otraT || 0) + Number(otraV || 0) + v.ajenas + v.sinDueno,
    sinDueno: v.sinDueno,
  };
}

/** Lo que suma la franja: {pendientes, porRevisar, otraCuenta}. */
export async function contarTodo(usuario) {
  const { elementos, otraCuenta } = await censoDePendientes(usuario);
  return {
    pendientes: elementos.filter((e) => e.estado === PENDIENTE || e.estado === ENVIANDO).length,
    porRevisar: elementos.filter((e) => e.estado === POR_REVISAR).length,
    paraTerminar: elementos.filter((e) => e.estado === PARA_TERMINAR).length,
    otraCuenta,
  };
}

/**
 * Reenvía TODO en el orden de D-7 y guarda en el espejo lo que las colas
 * viejas rechazaron. Devuelve {rechazados} con la forma que `AvisoRechazados`
 * ya conoce (movimientos y recepciones marcadas con `_tipo`).
 */
// Un envío por cuenta a la vez EN ESTA PESTAÑA (auditoría F3, P2-3): el
// envío automático de useConexion al volver la señal y «Enviar ahora»
// pulsado en ese momento salían como dos POST con la misma clave. El
// servidor los dejaba en una fila (idempotencia), pero era tráfico doble y
// una carrera. El segundo espera al que ya va y recibe su mismo resultado.
// Entre pestañas sigue abierto (PWA-F2-P2-1: navigator.locks).
//
// El candado CADUCA (segunda vuelta, P2-4): ningún fetch del cliente lleva
// tope, así que un POST que no responde nunca (socket muerto al cambiar de
// red) dejaba a toda llamada siguiente esperando la misma promesa hasta
// recargar. Pasado el tope —el del proxy, 30 s— el envío siguiente vuelve a
// salir. No se pierde ni se duplica nada: lo que no llegó sigue en su cola
// con la MISMA clave, y el servidor deja una fila por clave.
export const TOPE_CANDADO_ENVIO_MS = 30000;
const enviosEnVuelo = new Map();   // llave → { promesa, desde }
export function enviarTodo(usuario) {
  const id = usuario?.id;
  const llave = id === undefined || id === null ? null : String(id);
  const vivo = llave !== null ? enviosEnVuelo.get(llave) : null;
  if (vivo && Date.now() - vivo.desde < TOPE_CANDADO_ENVIO_MS) return vivo.promesa;
  const envio = enviarTodoAhora(usuario);
  if (llave === null) return envio;
  const registro = { desde: Date.now() };
  // Solo suelta el candado quien lo tiene: un envío colgado que responde
  // tarde no le quita el suyo al que lo relevó.
  registro.promesa = envio.finally(() => { if (enviosEnVuelo.get(llave) === registro) enviosEnVuelo.delete(llave); });
  enviosEnVuelo.set(llave, registro);
  return registro.promesa;
}

async function enviarTodoAhora(usuario) {
  if (globalThis.navigator?.onLine === false) return { rechazados: [] };
  const id = usuario?.id;
  if (id === undefined || id === null) return { rechazados: [] };
  // Las colas viejas se envían ENTERAS y con la sesión abierta: solo si todo
  // lo que guardan es de esta cuenta (P1-4).
  const viejas = await leerViejas();
  const { bloqueadas } = repartirViejas(usuario, viejas);
  const rechazados = [];
  if (!bloqueadas.has('movimiento')) {
    const m = await seguro(sincronizarMovimientos(), { rechazados: [] });
    if (m.rechazados?.length) { rechazados.push(...m.rechazados); await anotarPorRevisar('movimiento', m.rechazados, id); }
  }
  if (!bloqueadas.has('conteo')) await seguro(sincronizarConteos(api), null);
  if (!bloqueadas.has('recepcion')) {
    const r = await seguro(sincronizarRecepciones(), { rechazados: [] });
    if (r.rechazados?.length) { rechazados.push(...r.rechazados); await anotarPorRevisar('recepcion', r.rechazados, id); }
  }
  await seguro(sincronizarTraslados(id), null);
  if (!bloqueadas.has('lectura')) await seguro(sincronizarLecturas(), null);
  await seguro(sincronizarVentas(id), null);
  // Las colas nuevas avisan solas (colaBase); las viejas no. Sin este aviso,
  // «Pendientes de enviar» abierta seguía enseñando «por enviar» lo que ya
  // había llegado (lo midió auditar-pwa.sh --con-api, F3, 11 oct 2026).
  if (huellaViejas(viejas) !== huellaViejas(await leerViejas())) avisarCambio();
  return { rechazados };
}

function huellaViejas({ movs, conteos, recs, lects }) {
  return JSON.stringify([movs.length, conteos.length, recs.length, lects.map((l) => `${l.clave_idempotencia}:${l.estado}`)]);
}

/** Suscribirse a cualquier cambio en cualquier cola. Devuelve cómo dejar de hacerlo. */
export function suscribirCambios(fn) {
  ventana().addEventListener?.(EVENTO_CAMBIO, fn);
  return () => ventana().removeEventListener?.(EVENTO_CAMBIO, fn);
}
